/** Small drawing helpers shared by more than one theme. Nothing theme-specific lives here. */
import type { CellRect, PaintOptions, Theme } from './types.js';

/** A rounded-rectangle path, ready for `ctx.fill()` or `ctx.stroke()`. */
export function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const radius = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

/** `#RRGGBB` to `rgba(r, g, b, alpha)`. No support for shorthand or alpha hex — the tokens never use either. */
export function hexToRgba(hex: string, alpha: number): string {
  const value = hex.replace('#', '');
  const r = Number.parseInt(value.slice(0, 2), 16);
  const g = Number.parseInt(value.slice(2, 4), 16);
  const b = Number.parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * The default ghost painter: draw every ghost cell with `paintCell` in its
 * ghost state. `activeCells` is unused here — it exists on the interface for
 * a look that draws a line instead of cells (the retired Blueprint did).
 */
export function ghostByCell(paintCell: Theme['paintCell']): Theme['paintGhost'] {
  return (ctx, ghostCells, _activeCells, kind) => {
    for (const rect of ghostCells) {
      paintCell(ctx, rect, kind, { state: { kind: 'ghost' } });
    }
  };
}

/** Clamp a clear-animation progress cell state to its `progress` number, or `0` when not clearing. */
export function clearProgress(options: PaintOptions): number {
  return options.state.kind === 'clearing' ? options.state.progress : 0;
}

export function isGhost(options: PaintOptions): boolean {
  return options.state.kind === 'ghost';
}

/** Cell rect shrunk by an inset on every side. */
export function inset(rect: CellRect, by: number): { x: number; y: number; w: number; h: number } {
  return { x: rect.x + by, y: rect.y + by, w: rect.size - by * 2, h: rect.size - by * 2 };
}
